import { useState } from "react";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2 } from "lucide-react";

// Record a payment ALREADY RECEIVED outside SalesOS against ONE existing
// obligation — a plan instalment/off-plan line, or a pre-plan obligation
// such as Partnership's purpose-linked IELTS/PTE class fee. One shared
// dialog (extracted from Customer 360 Finance's original Record-payment
// dialog, same fields + test ids) so the Case Payments tab and Customer 360
// can never drift apart. Always POST /finance/payments (finance.record);
// the result is "recorded" until the separate finance.verify step.

export const inr = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`);
export const PAYMENT_METHODS = ["upi", "bank_transfer", "card", "cash", "cheque", "gateway"];
// One key per dialog opening — a double-click/retry of the same submission
// returns the SAME payment (finance.record_payment's idempotency_key).
export const newPaymentIdemKey = () => `recpay-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

// Purpose-linked obligations raised by a Process workflow
// (payment_obligations.purpose) — what the payment is for, and what its
// verification unlocks. Keyed off the stored purpose, never inferred.
export const PURPOSES = {
  ielts_pte_fee: {
    label: "Partnership IELTS/PTE class fee",
    unlocks: "Once this fee is verified paid in full, the Case's “Schedule IELTS/PTE class” task is created.",
  },
};

const OBL_BADGE = {
  pending: "bg-muted text-muted-foreground", due: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  partial: "bg-sky-500/15 text-sky-600 dark:text-sky-400", paid: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  overdue: "bg-red-500/15 text-red-600 dark:text-red-400", na: "bg-muted text-muted-foreground",
};

// Amount not yet covered by a verified OR a still-awaiting-verification
// payment — the "Record payment" action is only offered while this is > 0,
// so an already-recorded (unverified) payment isn't recorded a second time.
export const unrecordedAmount = (o, payments = []) => {
  const pending = payments
    .filter((p) => p.obligation_id === o.obligation_id && p.verification_status === "recorded" && !p.reversed)
    .reduce((s, p) => s + Number(p.amount), 0);
  return Number(o.expected_amount) - Number(o.amount_paid || 0) - pending;
};

export const openPaymentFor = (o) => ({ ...o, idempotency_key: newPaymentIdemKey() });

export function RecordObligationPaymentDialog({ obligation, onClose, onRecorded }) {
  const [pay, setPay] = useState({ amount: "", paid_at: new Date().toISOString().slice(0, 10), method: "upi", reference: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const purpose = PURPOSES[obligation?.purpose];

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/finance/payments", {
        plan_id: obligation.plan_id || null, obligation_id: obligation.obligation_id,
        amount: Number(pay.amount), paid_at: `${pay.paid_at}T12:00:00Z`, method: pay.method,
        reference: pay.reference || null, notes: pay.notes || null,
        idempotency_key: obligation.idempotency_key,
      });
      toast.success("Payment recorded — awaiting verification");
      setPay({ ...pay, amount: "", reference: "", notes: "" });
      onClose();
      onRecorded?.();
    } catch (e) { toast.error(errDetail(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!obligation} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="record-payment-dialog">
        <DialogHeader><DialogTitle>Record payment — {purpose?.label || obligation?.label}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {purpose && (
            <div className="rounded-md border p-2 text-xs space-y-0.5" data-testid="record-payment-purpose">
              <div><span className="text-muted-foreground">Payment for:</span> <b>{purpose.label}</b> <span className="text-muted-foreground">({obligation.label})</span></div>
              <div className="text-muted-foreground">{purpose.unlocks}</div>
            </div>
          )}
          <div className="text-xs text-muted-foreground">Expected {inr(obligation?.expected_amount)} · Outstanding {inr(obligation?.outstanding)}. Partial amounts are stored as separate immutable payments.</div>
          <div className="text-xs text-muted-foreground" data-testid="record-payment-manual-note">
            For money already received outside SalesOS (bank transfer, cash, cheque…). Nothing is sent to a payment gateway;
            the payment stays “recorded” until someone with payment-verification rights verifies it.
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Amount received</Label><Input data-testid="pay-amount" type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} /></div>
            <div><Label>Payment date</Label><Input data-testid="pay-date" type="date" value={pay.paid_at} onChange={(e) => setPay({ ...pay, paid_at: e.target.value })} /></div>
            <div><Label>Method</Label>
              <Select value={pay.method} onValueChange={(v) => setPay({ ...pay, method: v })}>
                <SelectTrigger data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map((x) => <SelectItem key={x} value={x}>{x.replaceAll("_", " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Transaction reference</Label><Input data-testid="pay-reference" value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} placeholder="UTR / txn id" /></div>
          </div>
          <div><Label>Notes</Label><Textarea data-testid="pay-notes" value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} /></div>
        </div>
        <DialogFooter><Button data-testid="pay-submit" onClick={submit} disabled={!pay.amount || busy}>{busy ? "Recording…" : "Record payment"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Whether "Record payment" is offered for this obligation right now.
export const canRecordAgainst = (o, payments, canRecord) =>
  !!canRecord && o.status !== "paid" && unrecordedAmount(o, payments) > 0.004;

// The individual payments on one obligation, each with its verification
// status and (finance.verify holders only) a Verify action.
export function ObligationPaymentRows({ obligation: o, payments = [], canVerify, onVerify }) {
  return payments.filter((p) => p.obligation_id === o.obligation_id).map((p) => (
    <div key={p.payment_id} className="flex flex-wrap items-center gap-2 rounded-md border px-3 py-1.5 text-xs" data-testid={`payment-${p.payment_id}`}>
      <span className="font-semibold">{inr(p.amount)}</span>
      <span className="text-muted-foreground">{fmtIST(p.paid_at)} · {p.method} · {p.reference || "no ref"}{p.recorded_by_name ? ` · recorded by ${p.recorded_by_name}` : ""}</span>
      <Badge className={p.verification_status === "verified" ? OBL_BADGE.paid : OBL_BADGE.due}>{p.verification_status}</Badge>
      {canVerify && p.verification_status === "recorded" && (
        <Button size="sm" variant="ghost" className="h-6 px-2" data-testid={`verify-${p.payment_id}`} onClick={() => onVerify(p.payment_id)}>
          <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Verify</Button>)}
    </div>
  ));
}

// One obligation (pre-plan / purpose-linked) with its individual payments,
// plus Record / Verify actions for the holders of finance.record /
// finance.verify respectively.
export function ObligationPaymentsBlock({ obligation: o, payments = [], canRecord, canVerify, onRecord, onVerify, testIdPrefix = "obl" }) {
  const purpose = PURPOSES[o.purpose];
  const canAdd = canRecordAgainst(o, payments, canRecord);
  return (
    <div className="rounded-md border p-2 text-sm space-y-1.5" data-testid={`${testIdPrefix}-${o.obligation_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        {purpose && <Badge variant="outline" data-testid={`${testIdPrefix}-purpose-${o.obligation_id}`}>{purpose.label}</Badge>}
        <span className="font-medium">{o.label}</span>
        <span className="text-xs text-muted-foreground">Verified {inr(o.amount_paid)} of {inr(o.expected_amount)} · Outstanding {inr(o.outstanding)}</span>
        <Badge className={OBL_BADGE[o.status] || ""}>{o.status}</Badge>
        {canAdd && (
          <Button size="sm" variant="outline" className="ml-auto" data-testid={`${testIdPrefix}-record-${o.obligation_id}`}
            onClick={() => onRecord(openPaymentFor(o))}>Record payment</Button>
        )}
      </div>
      {purpose && o.status !== "paid" && <div className="text-xs text-muted-foreground">{purpose.unlocks}</div>}
      <ObligationPaymentRows obligation={o} payments={payments} canVerify={canVerify} onVerify={onVerify} />
    </div>
  );
}
